import { Link } from '../../../types';
import { useEffect, useRef, useState } from 'react';
import ReactModalPopup from '../ReactModalPopup';
import { BACKEND_URL } from '../../../utils/constants';
import Skeleton, { CharityDonationTemplate } from '../Skeleton';
import CharityDonationFormStateListener from './CharityDonationFormStateListener';
import { getAttribution } from '../../../utils/attribution';

import s from './index.module.scss';

const LinkWithCWModal = (link: Link) => {
  const [open, setOpen] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(true);
  const ref = useRef<HTMLIFrameElement>(null);

  const onLoad = () => {
    // Hide "loading screen" at once when iframe data is loaded.
    setLoaderOpen(false);
  };

  const parts = link.url.split('?');
  const query = new URLSearchParams(parts[1]);
  const attribution = getAttribution();
  // Pass source code to the Charity modal.
  if (attribution && attribution.source_code && attribution.appeal_id) {
    query.append('cw_source_code', attribution.source_code);
    query.append('cw_appeal_id', String(attribution.appeal_id));
  }
  const charityDonationPath = query.get('charity_donation');
  query.delete('charity_donation');

  const [iframeUrlWithQuery, setIframeUrlWithQuery] = useState(
    `/charity-donation/${charityDonationPath}?` + query.toString(),
  );

  useEffect(() => {
    const pageshow = (event: PageTransitionEvent) => {
      // The persisted read-only property indicates if
      // a webpage is loading from a cache.
      if (event.persisted) {
        setOpen(false);
        setLoaderOpen(true);
      }
    };

    window.addEventListener('pageshow', pageshow);

    const currentQuery = new URLSearchParams(window.location.search);
    if (currentQuery.get('charity_donation_error') === 'failed') {
      const charity_donation_url = currentQuery.get('charity_donation_url');
      const payment_gateway = currentQuery.get('payment_gateway') ?? 'undefined';
      if (charity_donation_url?.startsWith('/charity-donation/')) {
        setIframeUrlWithQuery(
          charity_donation_url +
            '?error=payment&payment_gateway=' +
            encodeURIComponent(payment_gateway),
        );
        setOpen(true);
        setLoaderOpen(true);

        // Remove "charity_donation_error" from the URL.
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState(null, '', url.toString());
      }
    }

    return () => {
      window.removeEventListener('pageshow', pageshow);
    };
  }, []);

  return (
    <>
      <div
        data-analytics="open-charity-donation-modal"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
          setLoaderOpen(true);
        }}
      >
        {link?.children}
      </div>
      <ReactModalPopup
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        className={s.content}
        overlayClassName={s.overlay}
        shouldCloseOnOverlayClick={false}
      >
        <Skeleton open={loaderOpen}>
          <CharityDonationTemplate />
        </Skeleton>
        {open && <CharityDonationFormStateListener />}
        <iframe
          ref={ref}
          title="Concern Worldwide modal"
          src={`${BACKEND_URL}${iframeUrlWithQuery}&page_url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '')}`}
          width="100%"
          height="100%"
          onLoad={onLoad}
          style={{
            display: 'block',
            width: '100%',
            height: loaderOpen ? '0' : '100vh',
            border: 'none',
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: 'transparent',
            opacity: loaderOpen ? 0 : 1,
          }}
          allowTransparency
          allow="payment * publickey-credentials-get *"
          sandbox="allow-forms allow-top-navigation allow-top-navigation-by-user-activation allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
          data-component="charity-donation-modal-iframe"
        >
          Error during loading Concern Worldwide modal
        </iframe>
      </ReactModalPopup>
    </>
  );
};

export default LinkWithCWModal;
